"use client";

import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { MonthlyRevenueData } from "@/api/revenue/get-monthly-revenue";

const chartConfig = {
    "Total Income": {
        label: "Total Income",
        color: "#3b82f6", // Modern blue
    },
} satisfies ChartConfig;

interface MonthlyRevenueChartProps {
    data: MonthlyRevenueData[];
}

export function MonthlyRevenueChart({ data }: MonthlyRevenueChartProps) {
    // Format the data for the chart
    const chartData = data.map(item => {
        // Extract month name from the YYYY-MM format
        const [year, month] = item.month.split("-");
        const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const monthName = monthNames[parseInt(month) - 1] || month;

        return {
            name: `${monthName} ${year}`,
            "Total Income": item.total_income
        };
    });

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    return (
        <Card className="@container/card">
            <CardHeader>
                <CardTitle>Monthly Revenue</CardTitle>
                <CardDescription>
                    <span className="hidden @[540px]/card:block">Revenue for the selected period</span>
                    <span className="@[540px]/card:hidden">Revenue overview</span>
                </CardDescription>
            </CardHeader>
            <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
                <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
                    <BarChart
                        accessibilityLayer
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 60,
                        }}
                    >
                        <defs>
                            <linearGradient id="fillTotalIncome" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} />
                        <XAxis 
                            dataKey="name" 
                            angle={-45} 
                            textAnchor="end" 
                            height={60}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <ChartTooltip 
                            cursor={false}
                            content={
                                <ChartTooltipContent 
                                    formatter={(value) => [formatCurrency(Number(value)), "Total Income"]}
                                    labelFormatter={(label) => `Month: ${label}`}
                                />
                            }
                        />
                        <Bar dataKey="Total Income" fill="url(#fillTotalIncome)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}